import type { LessonListItem } from '@/types/apis/Lesson';

import React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

import { Button } from '../ui/button';

import { Container } from './container';
import SearchBar from './searchBar';
import SearchResult from './searchResult';

import useEducationContent from '@/hooks/queries/useEducationContent';
import useTranslations from '@/hooks/useTranslation';
import routes from '@/statics/routes';
import ArrowRightIcon from '@/svgs/ArrowBack.svg';
import BookmarkIcon from '@/svgs/bookmark.svg';
import LocationIcon from '@/svgs/logo.svg';

const useEducationSearch = (
  query: string
): { results: LessonListItem[]; isFetched: boolean; isFetching: boolean } => {
  const { data, isFetched, isFetching } = useEducationContent({
    queries: { search: query, pageSize: 20 },
    enabled: Boolean(query),
  });
  return { results: data?.pages[0].list || [], isFetched, isFetching };
};

type HeaderProps = { withBackButton: boolean; searchLess: boolean };

const BookmarkButton: React.FC = () => {
  const { t } = useTranslations('routes');

  return (
    <Button variant="default" size="sm" asChild className="p-2">
      <Link href={routes.bookmarks.url}>
        <p>{t('bookmarks')}</p>
        <div className="w-5 h-5 flex items-center justify-center">
          <BookmarkIcon className="size-5" />
        </div>
      </Link>
    </Button>
  );
};

const BackButton: React.FC = () => {
  const { t } = useTranslations('routes');
  const router = useRouter();

  return (
    <Button
      variant="default"
      size="sm"
      asChild
      className="p-2"
      onClick={() => router.back()}
    >
      <Link href={routes.home.url}>
        <div className="w-5 h-5 flex items-center justify-center">
          <ArrowRightIcon className="size-5" />
        </div>
        <p>{t('return')}</p>
      </Link>
    </Button>
  );
};

const Header: React.FC<HeaderProps> = ({ withBackButton, searchLess }) => {
  const { t } = useTranslations('components.header');

  return (
    <Container className="flex gap-[30px] flex-col">
      <div className="justify-between flex items-center">
        <Link className="flex items-center gap-[10px]" href={routes.home.url}>
          <div className="w-[40px] h-[26px] flex items-center justify-center [&>svg]:w-full">
            <LocationIcon />
          </div>
          <p className="font-bold primary">{t('mets')}</p>
        </Link>
        {!withBackButton && <BookmarkButton />}
        {withBackButton && <BackButton />}
      </div>
      {!searchLess && (
        <SearchBar
          useSearch={useEducationSearch}
          renderResult={(item) => (
            <SearchResult
              key={item.id}
              title={item.title}
              description={item.description!}
              image={item.image || item.video_cover_image!}
              href={routes.lesson.href(item.id)}
            />
          )}
        />
      )}
    </Container>
  );
};

export { BookmarkButton, Header };
